<template>
    <div class="DocumentDataReviewEntitySummary" :class="{ 'DocumentDataReviewEntitySummary--scrolled': isScrolledDown }">
        <div class="DocumentDataReviewEntitySummary__header">
            <div class="flex items-center gap-2 text-lg text-eidDarkBlue font-medium">
                <DocumentDataReviewBackgroundIcon :icon="entitySummaryIcon" size="normal" />Entity Summary
            </div>
            <NewButton
                variant="flat"
                @click="goToEntityDetails"
            >
                <EidIcon :icon-src="iconArrowLeft" />
                Go to entity details
            </NewButton>
            <NewButton
                v-if="customPropertiesFeatureFlag && customPropertyCards.length > 0"
                variant="flat"
                @click="displayCustomProperties = !displayCustomProperties"
            >
                {{ displayCustomProperties ? 'Hide details' : 'View Custom Properties' }}
                <EidIcon :icon-src="displayCustomProperties ? iconChevronUp : iconChevronDown" />
            </NewButton>
        </div>
        <div class="DocumentDataReviewEntitySummary__detailsGrid">
            <DocumentDataReviewPropertyCard
                v-for="(item, idx) in summaryItemCards"
                :key="idx"
                :label="item.label"
                :value="item.value"
                :icon="item.icon"
                :is-small="isScrolledDown"
            />
        </div>
        <template v-if="displayCustomProperties">
            <div class="h-[1px] w-full bg-eidSecureBlue-10" />
            <div class="DocumentDataReviewEntitySummary__additionalDetailsTitle">
                Additional Details
            </div>
            <div class="DocumentDataReviewEntitySummary__detailsGrid">
                <DocumentDataReviewPropertyCard
                    v-for="(item, idx) in customPropertyCards"
                    :key="idx"
                    :label="item.label"
                    :value="item.value"
                    :icon="item.icon"
                    :is-small="isScrolledDown"
                />
            </div>
        </template>
        <DocumentDataReviewErrors v-if="showErrors" :document-data-review-id="documentDataReviewId" />
        <DocumentDataReviewGoToEntityDetailsModal
            v-if="showGoToEntityDetailsModal"
            @input="handleGoToEntityDetailsConfirm"
            @close="showGoToEntityDetailsModal = false"
        />
    </div>
</template>

<style lang="scss">
@mixin header-shown {
    max-height: 200px;
    opacity: 1;
    overflow: hidden;
}

@mixin header-animation {
    transition:
        max-height 300ms cubic-bezier(0.4, 0, 0.2, 1),
        opacity 300ms cubic-bezier(0.4, 0, 0.2, 1),
        padding 300ms cubic-bezier(0.4, 0, 0.2, 1),
        margin 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

@mixin header-hidden {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: -21px;
}

.DocumentDataReviewEntitySummary {
    display: flex;
    flex-direction: column;
    background: var(--eidWhite);
    padding: 21px;
    gap: 21px;

    &--scrolled {
        position: sticky;
        // minus 16px to account for the padding pageWrapper so that the summary covers the empty space of the padding
        top: -16px;
        z-index: 10;
        border-bottom: 1px solid var(--eidSecureBlue-10);

        .DocumentDataReviewEntitySummary__header {
            @include header-hidden;
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        font-size: 18px;
        line-height: 28px;
        font-weight: 500;
        color: var(--eidDarkBlue);
        @include header-shown;
        @include header-animation;
    }

    &__detailsGrid {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 24px;
        transition-property: all;
        transition-duration: 300ms;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }

    &__additionalDetails {
        border-width: 0 0 1px 0;
        border-top: 1px solid var(--eidSecureBlue-10);
        padding-top: 21px;
    }

    &__additionalDetailsTitle {
        font-size: 14px;
        color: var(--eidTechGray);
        font-weight: 500;
        position: relative;
        padding-left: 10px;
        display: flex;
        align-items: center;

        &::before {
            content: "";
            display: inline-block;
            width: 4px;
            height: 100%;
            border-radius: 0.625rem;
            left: 0;
            top: 0;
            position: absolute;
            background-color: var(--eidTrustGreen);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { debounce } from 'lodash';

    import { faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons';
    import {
        faCars, faCircleDot, faEnvelope as faEnvelopeIcon, faEnvelope, faFileCertificate, faForklift,
        faBuilding, faFileLines, faFileText, faHashtag, faLocationDot, faMapMarkerAlt, faPhone, faUser, IconDefinition,
        faArrowLeft,
    } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { EntityDocumentDataReview, CustomPropertyType, CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

    import NewButton from '@/components/new-button/NewButton.vue';
    import DocumentDataReviewBackgroundIcon
        from '@/modules/document-data-review/components/commons/document-data-review/sub-components/DocumentDataReviewBackgroundIcon.vue';
    import DocumentDataReviewPropertyCard
        from '@/modules/document-data-review/components/commons/document-data-review/sub-components/DocumentDataReviewPropertyCard.vue';
    import { EntityPropertyCardDetails } from '@/modules/document-data-review/models/EntityPropertyCardDetails.model';
    import { getDisplayAddress } from '@/modules/entity-details/utils/getDisplayAddress';
    import { entitySchemas } from '@/modules/entity-management/utils/build-entity-json-schema/constants';

    import DocumentDataReviewErrors from './DocumentDataReviewErrors.vue';
    import DocumentDataReviewGoToEntityDetailsModal from './DocumentDataReviewGoToEntityDetailsModal.vue';

    const customPropertiesIconsMap: Record<CustomPropertyType, IconDefinition> = {
        [CustomPropertyType.integer]: faHashtag,
        [CustomPropertyType.number]: faHashtag,
        [CustomPropertyType.boolean]: faCircleDot,
        [CustomPropertyType.collaterals]: faCars,
        [CustomPropertyType.string]: faFileLines,
        [CustomPropertyType.listString]: faFileLines,
        [CustomPropertyType.listEmail]: faEnvelopeIcon,
        [CustomPropertyType.address]: faLocationDot,
        [CustomPropertyType.credentialTypes]: faFileCertificate,
        [CustomPropertyType.email]: faEnvelopeIcon,
        [CustomPropertyType.enum]: faFileLines,
        [CustomPropertyType.equipment]: faForklift,
    };

    export default defineComponent({
        name: 'DocumentDataReviewEntitySummary',
        components: {
            DocumentDataReviewBackgroundIcon,
            DocumentDataReviewPropertyCard,
            DocumentDataReviewErrors,
            DocumentDataReviewGoToEntityDetailsModal,
            NewButton,
            EidIcon,
        },
        props: {
            entity: {
                type: Object as PropType<EntityDocumentDataReview>,
                required: true,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                required: true,
            },
            documentDataReviewId: {
                type: String as PropType<string>,
                required: true,
            },
            showErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        data() {
            return {
                iconChevronDown: faAngleDown,
                iconChevronUp: faAngleUp,
                iconArrowLeft: faArrowLeft,
                entitySummaryIcon: faBuilding,
                // Custom properties got removed from the first iteration so we disable this feature flag
                customPropertiesFeatureFlag: false,
                displayCustomProperties: false,
                isScrolledDown: false,
                scrollContainer: null as HTMLElement | null,
                debouncedHandleScroll: null as any,
                showGoToEntityDetailsModal: false,
            };
        },
        computed: {
            showCustomProperties(): boolean {
                return this.displayCustomProperties &&
                    this.customPropertiesFeatureFlag &&
                    this.customPropertyCards.length > 0;
            },
            summaryItemCards(): EntityPropertyCardDetails[] {
                return [
                    { label: 'Legal Name', value: this.entity?.legalName || '-', icon: faFileText },
                    { label: 'Primary Contact', value: this.entity?.contactName || '-', icon: faUser },
                    { label: 'Address',
                      value: getDisplayAddress({ schema: entitySchemas.address, value: this.entity?.address }),
                      icon: faMapMarkerAlt,
                    },
                    { label: 'Phone', value: this.entity?.contactPhoneNumber || '-', icon: faPhone },
                    { label: 'Email', value: this.entity?.contactEmail || '-', icon: faEnvelope },
                ];
            },
            customPropertyCards(): EntityPropertyCardDetails[] {
                const customProperties = this.entity?.customProperties || {};
                const cards: EntityPropertyCardDetails[] = Object.entries(customProperties).map(([ key, value ]) => {
                    const customPropertyType = this.customProperties.find(
                        ({ key: customPropertyKey }: CustomProperty) => customPropertyKey === key,
                    );
                    if (!customPropertyType) {
                        throw new Error(`Custom property type not found for key: ${key}`);
                    }
                    const card: EntityPropertyCardDetails = {
                        label: customPropertyType.name,
                        value: value as string,
                        icon: customPropertiesIconsMap[customPropertyType.type as CustomPropertyType],
                    };
                    return card;
                });
                return cards;
            },
        },
        created() {
            this.debouncedHandleScroll = debounce(this.handleScroll, 10);
        },
        mounted() {
            this.handleScroll();
            this.scrollContainer = this.$el.closest('.PageWrapper');
            if (this.scrollContainer) {
                this.scrollContainer.addEventListener('scroll', this.debouncedHandleScroll, { passive: true });
            }
        },
        beforeUnmount() {
            if (this.scrollContainer) {
                this.scrollContainer.removeEventListener('scroll', this.debouncedHandleScroll);
            }
            if (this.debouncedHandleScroll) {
                this.debouncedHandleScroll.cancel();
            }
        },
        methods: {
            goToEntityDetails(): void {
                this.showGoToEntityDetailsModal = true;
            },
            handleGoToEntityDetailsConfirm(confirmed: boolean): void {
                this.showGoToEntityDetailsModal = false;
                if (confirmed) {
                    this.$router.push({
                        name: 'dashboard',
                        params: { rpId: (this as any).$rp.current },
                        query: { entity: this.entity.id },
                    });
                }
            },
            handleScroll(): void {
                if (!this.scrollContainer) {
                    return;
                }

                const headerElement = document.querySelector('.DocumentDataReviewEntitySummary__header');
                if (!headerElement) {
                    console.warn('DocumentDataReviewEntitySummary__header element not found');
                    return;
                }

                const containerScrollTop = this.scrollContainer.scrollTop;
                const headerRect = headerElement.getBoundingClientRect();
                const headerHeight = headerRect.height;
                const eightyPercentThreshold = headerHeight * 0.2;
                const headerPassedThreshold = containerScrollTop > 0 && containerScrollTop > eightyPercentThreshold;
                this.isScrolledDown = headerPassedThreshold;
            },
        },
    });
</script>
